import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Box, Grid, Button, TextField, Link, InputAdornment, Divider, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { Icon } from '@iconify/react';
import Loader from '../../../components/loader';
import { countryManagementGetById } from '../countryManagement.service';
import AdministrativeLocationTypesManager from '../components/AdministrativeLocationTypesManager';
import GeographicalDataLoader from '../components/GeographicalDataLoader';
import OptionalLevelsManager from '../components/OptionalLevelsManager';
import {
  FieldLabel,
  formFieldSx,
  sectionTitleSx,
} from '../../../styles/formStyles';
import {
  createEditBackButtonSx,
  createEditCardSx,
  createEditPageHeaderSx,
  createEditPageShellSx,
  createEditPageTitleSx,
} from '../../../styles/pageFormStyles';

const fieldSx = formFieldSx;

const sasToken = process.env.REACT_APP_AZURE_SAS_TOKEN;

function DisplayField({ label, value, multiline = false, minRows = 1 }) {
  return (
    <Box>
      <FieldLabel label={label} />
      <TextField
        value={value || '-'}
        fullWidth
        multiline={multiline}
        minRows={minRows}
        sx={fieldSx}
        InputProps={{
          readOnly: true,
        }}
      />
    </Box>
  );
}

function DisplayFileLink({ label, fileUrl }) {
  const hasFile = fileUrl && typeof fileUrl === 'string' && fileUrl.trim();

  const getFileNameFromUrl = (url) => {
    if (!url) return '';
    // Remove query parameters
    const urlWithoutParams = url.split('?')[0];
    // Get the last part after the last slash
    let fileName = urlWithoutParams.split('/').pop() || '';

    // Decode URL-encoded characters if needed
    try {
      fileName = decodeURIComponent(fileName);
    } catch (e) {
      // If decoding fails, use the original
    }

    // Remove UUID prefix (everything before the first underscore)
    const underscoreIndex = fileName.indexOf('_');
    if (underscoreIndex !== -1) {
      fileName = fileName.substring(underscoreIndex + 1);
    }

    return fileName;
  };

  const handleFileDownload = () => {
    if (hasFile) {
      let finalUrl = fileUrl;

      // Append SAS token if available
      if (sasToken) {
        const separator = fileUrl.includes('?') ? '&' : '?';
        finalUrl = `${fileUrl}${separator}${sasToken}`;
      }

      const link = document.createElement('a');
      link.href = finalUrl;
      link.download = getFileNameFromUrl(fileUrl);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const fileName = getFileNameFromUrl(fileUrl);
  const linkText = fileName || 'Download File';

  return (
    <Box>
      <FieldLabel label={label} />
      <TextField
        fullWidth
        value={hasFile ? fileName : '-'}
        sx={{
          ...fieldSx,
          '& .MuiInputBase-input': {
            overflow: 'hidden',
            textOverflow: 'clip',
            whiteSpace: 'nowrap',
          },
        }}
        variant="outlined"
        InputProps={{
          readOnly: true,
          endAdornment: hasFile && (
            <InputAdornment position="end" sx={{ gap: 1, display: 'flex', alignItems: 'center' }}>
              <Divider orientation="vertical" flexItem sx={{ my: 0.5, borderColor: 'rgba(0, 0, 0, 0.23)' }} />
              <Link
                component="button"
                type="button"
                onClick={(e) => { e.preventDefault(); handleFileDownload(); }}
                sx={{ cursor: 'pointer', fontSize: '0.85rem', color: '#294190', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
              >
                Download
              </Link>
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

const CountryManagementView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [countryData, setCountryData] = useState({});
  const [loading, setLoading] = useState(false);

  const getSafeValue = (value) => {
    if (value === null || value === undefined) return '-';
    const text = String(value).trim();
    return text || '-';
  };

  const getcountryData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await countryManagementGetById(id);
      setCountryData(data.data || {});
    } catch (error) {
      console.error('Error fetching country by ID:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getcountryData();
  }, [getcountryData]);

  return (
    <Box sx={createEditPageShellSx}>
      <Box sx={createEditPageHeaderSx}>
        <Button
          onClick={() => navigate('/countryManagement')}
          startIcon={<Icon icon="mdi:arrow-left" width="20" height="20" />}
          sx={createEditBackButtonSx}
        >
          Back
        </Button>
        <Grid container alignItems="center" spacing={2}>
          <Grid size={{ xs: 12, md: 8 }}>
            <Typography className="app-page-title" sx={createEditPageTitleSx}>
              Country Setup Details
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} textAlign={{ xs: 'left', md: 'right' }}>
            <Button
              onClick={() => navigate(`/countryManagementCreation/${id}?type=edit`)}
              startIcon={<span className="material-symbols-rounded">edit</span>}
              sx={{
                minWidth: { xs: '100%', sm: 86 },
                height: 40,
                borderRadius: '12px',
                backgroundColor: '#DFE5F6',
                color: '#294190',
                textTransform: 'none',
                fontSize: 16,
                lineHeight: '24px',
                letterSpacing: '0.15px',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: '#DFE5F6',
                },
                '& .material-symbols-rounded': {
                  fontSize: 18,
                },
              }}
            >
              Edit
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Country Details
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={12}>
            <DisplayField
              label="Country Name"
              value={getSafeValue(countryData?.countryName || countryData?.name)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayField label="Region" value={getSafeValue(countryData?.regionName)} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayField label="Currency" value={getSafeValue(countryData?.currencyCode || countryData?.currency)} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayField label="Country Code (ISO)" value={getSafeValue(countryData?.isoCode)} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayField label="Timezone" value={getSafeValue(countryData?.timeZone || countryData?.timezone)} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayFileLink label="Terms and Conditions" fileUrl={countryData?.termsAndConditionsUrl || countryData?.termsAndConditions} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <DisplayFileLink label="Privacy Policy" fileUrl={countryData?.privacyPolicyUrl || countryData?.privacyPolicy} />
          </Grid>
        </Grid>

      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Administrative Location Types
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={12}>
            <AdministrativeLocationTypesManager
              levels={countryData?.administrativeLocationTypes || []}
              onChange={() => {}}
              readOnly={true}
            />
          </Grid>
        </Grid>
      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Geographical Data
        </Typography>

        <Grid container spacing={3} alignItems="flex-start">
          <Grid size={12}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              The actual states, districts and blocks recorded under the levels above. Until these are
              loaded, nothing in the system can be given an address in this country.
            </Typography>
            <GeographicalDataLoader countryId={id} />

            <Button
              onClick={() => navigate(`/countryLocations/${id}`)}
              startIcon={<Icon icon="mdi:format-list-bulleted" width="18" height="18" />}
              sx={{
                mt: 2,
                height: 40,
                borderRadius: '12px',
                backgroundColor: '#DFE5F6',
                color: '#294190',
                textTransform: 'none',
                fontSize: 15,
                fontWeight: 500,
                '&:hover': { backgroundColor: '#DFE5F6' },
              }}
            >
              View &amp; Edit Locations
            </Button>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
              Browse, correct or add locations by hand. Needed for a country we hold no reference data
              for, and for fixing a name the reference data gets wrong.
            </Typography>
          </Grid>
        </Grid>
      </Box>

      <Box sx={createEditCardSx}>
        <Typography sx={sectionTitleSx}>
          Optional Address Levels
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Extra levels shown only by the apps they are switched on for, such as Gram Panchayat after Block
          for Abhigyan India. The levels above and every other app are not affected.
        </Typography>
        <OptionalLevelsManager countryId={id} levels={countryData?.administrativeLocationTypes || []} />
      </Box>

      {loading && <Loader />}
    </Box >
  );
};

export default CountryManagementView;
